import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { Radio, Eye, Users as UsersIcon, Globe, FileText, Smartphone, Megaphone, Target, MonitorSmartphone } from 'lucide-react';
import { analyticsAPI } from '../../api/api';

const CHANNEL_LABELS = {
  direct: 'Direct', search: 'Organic Search', social: 'Social',
  referral: 'Referral', campaign: 'Campaigns (UTM)',
};
const CHANNEL_COLORS = {
  direct: '#2B4C6F', search: '#10B981', social: '#8B5CF6',
  referral: '#F59E0B', campaign: '#D95800',
};

const RANGES = [
  { days: 7, label: '7D' },
  { days: 30, label: '30D' },
  { days: 90, label: '90D' },
];

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="bg-white rounded-xl shadow-md p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <Icon className={`h-8 w-8 ${accent}`} />
      </div>
      <p className="text-3xl font-bold text-gray-900">{value.toLocaleString()}</p>
      <p className="text-gray-500 text-sm">{label}</p>
    </div>
  );
}

function TopList({ title, icon: Icon, rows, nameFormatter, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <div className="bg-white rounded-xl shadow-md p-6" data-testid={testId}>
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-[#2B4C6F]" /> {title}
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">No data yet</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="relative">
              <div className="absolute inset-0 bg-[#2B4C6F]/10 rounded" style={{ width: `${(r.views / max) * 100}%` }} />
              <div className="relative flex items-center justify-between px-2 py-1.5 text-sm">
                <span className="font-medium text-gray-800 truncate mr-2">{nameFormatter ? nameFormatter(r.name) : r.name}</span>
                <span className="font-bold text-gray-900 shrink-0">{r.views.toLocaleString()}
                  <span className="text-gray-400 font-normal ml-1.5 text-xs">{r.visitors} visitors</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrafficTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [res, fRes] = await Promise.all([
        analyticsAPI.summary(days),
        analyticsAPI.funnels(days),
      ]);
      setData(res.data);
      setFunnelData(fRes.data);
    } catch (e) {
      console.error('Analytics fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return <div className="text-center py-16 text-gray-500 font-bold" data-testid="traffic-loading">Loading traffic data...</div>;
  }
  if (!data) return <div className="text-center py-16 text-gray-500">Failed to load analytics.</div>;

  const { totals, daily, channels, sources, pages, devices, campaigns } = data;
  const channelTotal = Math.max(1, channels.reduce((s, c) => s + c.views, 0));

  return (
    <div className="space-y-6" data-testid="traffic-tab">
      {/* Range toggle + live */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={`px-4 py-2 rounded-lg font-bold text-sm border-2 transition-all ${
                days === r.days ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`range-${r.days}d`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg shadow px-4 py-2" data-testid="live-visitors">
          <Radio className="h-4 w-4 text-red-500 animate-pulse" />
          <span className="font-bold text-gray-900">{totals.live}</span>
          <span className="text-gray-500 text-sm">live (30 min)</span>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Eye} label={`Page Views (${days}d)`} value={totals.views} accent="text-[#2B4C6F]" testId="stat-views" />
        <StatCard icon={UsersIcon} label={`Unique Visitors (${days}d)`} value={totals.visitors} accent="text-emerald-500" testId="stat-visitors" />
        <StatCard icon={Eye} label="Views / Visitor" value={totals.visitors ? Math.round((totals.views / totals.visitors) * 10) / 10 : 0} accent="text-[#D95800]" testId="stat-ratio" />
      </div>

      {/* Traffic over time */}
      <div className="bg-white rounded-xl shadow-md p-6" data-testid="traffic-chart">
        <h3 className="font-bold text-gray-900 mb-4">Traffic over time</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="views" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2B4C6F" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#2B4C6F" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="visitors" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#D95800" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#D95800" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip />
            <Area type="monotone" dataKey="views" stroke="#2B4C6F" strokeWidth={2} fill="url(#views)" name="Page Views" />
            <Area type="monotone" dataKey="visitors" stroke="#D95800" strokeWidth={2} fill="url(#visitors)" name="Visitors" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Channels bar */}
      <div className="bg-white rounded-xl shadow-md p-6" data-testid="channels-breakdown">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Globe className="h-5 w-5 text-[#2B4C6F]" /> Where traffic comes from</h3>
        <div className="flex h-6 rounded-lg overflow-hidden mb-4">
          {channels.map((c) => (
            <div key={c.name} style={{ width: `${(c.views / channelTotal) * 100}%`, backgroundColor: CHANNEL_COLORS[c.name] || '#999' }} title={`${CHANNEL_LABELS[c.name] || c.name}: ${c.views}`} />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {channels.map((c) => (
            <div key={c.name} className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: CHANNEL_COLORS[c.name] || '#999' }} />
              <div>
                <p className="font-bold text-gray-900">{CHANNEL_LABELS[c.name] || c.name}</p>
                <p className="text-gray-500 text-xs">{c.views.toLocaleString()} views · {Math.round((c.views / channelTotal) * 100)}%</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Conversion Funnels */}
      {funnelData && (
        <div className="bg-white rounded-xl shadow-md p-6" data-testid="conversion-funnels">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-[#D95800]" /> Conversion Funnels
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {funnelData.funnels.map((f) => (
              <div key={f.key} className="border-2 border-gray-100 rounded-xl p-4" data-testid={`funnel-${f.key}`}>
                <p className="font-bold text-gray-900 text-sm mb-3">{f.label}</p>
                {[
                  { label: 'Site visitors', value: f.visitors, pct: 100 },
                  { label: `Viewed ${f.page}`, value: f.reached, pct: f.reach_rate },
                  { label: 'Converted', value: f.converted, pct: f.visitors ? Math.round((f.converted / f.visitors) * 1000) / 10 : 0 },
                ].map((step, i) => (
                  <div key={i} className="mb-2">
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-gray-500">{step.label}</span>
                      <span className="font-bold text-gray-900">{step.value.toLocaleString()} <span className="text-gray-400 font-normal">({step.pct}%)</span></span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${i === 2 ? 'bg-emerald-500' : i === 1 ? 'bg-[#2B4C6F]' : 'bg-gray-300'}`} style={{ width: `${Math.min(100, step.pct)}%` }} />
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-500 mt-2">
                  Funnel completion: <span className="font-bold text-emerald-600">{f.conversion_rate}%</span> of funnel viewers
                </p>
              </div>
            ))}
          </div>

          {/* Which channel converts */}
          <h4 className="font-bold text-gray-900 text-sm mb-3">Which traffic source converts (first touch)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="channel-conversion-table">
              <thead>
                <tr className="border-b-2 border-gray-100 text-left">
                  <th className="py-2 pr-4 font-bold text-xs uppercase text-gray-500">Channel</th>
                  <th className="py-2 pr-4 font-bold text-xs uppercase text-gray-500 text-right">Visitors</th>
                  <th className="py-2 pr-4 font-bold text-xs uppercase text-gray-500 text-right">Conversions</th>
                  <th className="py-2 font-bold text-xs uppercase text-gray-500 text-right">Rate</th>
                </tr>
              </thead>
              <tbody>
                {funnelData.by_channel.map((c) => (
                  <tr key={c.channel} className="border-b border-gray-50">
                    <td className="py-2 pr-4 font-medium text-gray-800 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHANNEL_COLORS[c.channel] || '#999' }} />
                      {CHANNEL_LABELS[c.channel] || c.channel}
                    </td>
                    <td className="py-2 pr-4 text-right text-gray-700">{c.visitors.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right font-bold text-gray-900">{c.conversions.toLocaleString()}</td>
                    <td className={`py-2 text-right font-bold ${c.rate > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>{c.rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PWA Install Funnel */}
      {funnelData?.pwa && (
        <div className="bg-white rounded-xl shadow-md p-6" data-testid="pwa-install-funnel">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MonitorSmartphone className="h-5 w-5 text-[#D95800]" /> App Install Funnel (PWA)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <div className="border-2 border-gray-100 rounded-xl p-4" data-testid="pwa-stat-shown">
              <p className="text-2xl font-bold text-gray-900">{funnelData.pwa.shown.toLocaleString()}</p>
              <p className="text-gray-500 text-xs font-medium">Prompt Seen</p>
            </div>
            <div className="border-2 border-gray-100 rounded-xl p-4" data-testid="pwa-stat-installed">
              <p className="text-2xl font-bold text-emerald-600">{funnelData.pwa.installed.toLocaleString()}</p>
              <p className="text-gray-500 text-xs font-medium">App Installed</p>
            </div>
            <div className="border-2 border-gray-100 rounded-xl p-4" data-testid="pwa-stat-dismissed">
              <p className="text-2xl font-bold text-gray-400">{funnelData.pwa.dismissed.toLocaleString()}</p>
              <p className="text-gray-500 text-xs font-medium">Dismissed</p>
            </div>
            <div className="border-2 border-gray-100 rounded-xl p-4" data-testid="pwa-stat-rate">
              <p className="text-2xl font-bold text-[#D95800]">{funnelData.pwa.install_rate}%</p>
              <p className="text-gray-500 text-xs font-medium">Install Rate</p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Prompt Seen', value: funnelData.pwa.shown, pct: 100, color: 'bg-gray-300' },
              { label: 'App Installed', value: funnelData.pwa.installed, pct: funnelData.pwa.install_rate, color: 'bg-emerald-500' },
            ].map((step) => (
              <div key={step.label}>
                <div className="flex justify-between text-xs mb-0.5">
                  <span className="text-gray-500">{step.label}</span>
                  <span className="font-bold text-gray-900">{step.value.toLocaleString()} <span className="text-gray-400 font-normal">({step.pct}%)</span></span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${step.color}`} style={{ width: `${Math.min(100, step.pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Unique visitors who saw the "Add to Home Screen" prompt vs. those who installed the app ({days}d).
          </p>
        </div>
      )}

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopList title="Top Sources" icon={Globe} rows={sources} testId="top-sources" />
        <TopList title="Top Pages" icon={FileText} rows={pages} testId="top-pages" />
        <TopList title="Devices" icon={Smartphone} rows={devices} nameFormatter={(n) => n.charAt(0).toUpperCase() + n.slice(1)} testId="device-split" />
        <TopList title="UTM Campaigns" icon={Megaphone} rows={campaigns} testId="top-campaigns" />
      </div>
    </div>
  );
}
